/**
 * 颜色解析工具 - 解决 html2canvas 不支持 CSS 变量的问题
 * 遵循项目规范，避免颜色硬编码
 */

// theme-check-disable - 此文件是颜色解析工具，需要包含颜色值以解决html2canvas不支持CSS变量的问题
// 从 tokens.css 中提取的颜色映射表
const COLOR_TOKENS: Record<string, string> = {
  // Primary Colors
  '--primary-color': '#c41e3a',
  '--color-primary': '#c41e3a',
  '--primary-color-dark': '#8b1538',
  '--color-primary-dark': '#8b1538',
  '--primary-color-darker': '#6d1129',
  '--color-primary-darker': '#6d1129',
  
  // Secondary Colors
  '--secondary-color': '#6f42c1',
  '--color-secondary': '#6f42c1',
  '--secondary-color-dark': '#5a2d91',
  '--color-secondary-dark': '#5a2d91',
  
  // Success Colors
  '--success-color': '#28a745',
  '--color-success': '#28a745',
  '--success-color-light': '#20c997',
  '--success-bg': '#d4edda',
  '--color-success-light': '#d4edda',
  '--success-bg-light': '#f8fff8',
  '--success-text': '#155724',
  '--color-success-dark': '#155724',
  
  // Warning Colors
  '--warning-color': '#ffc107',
  '--color-warning': '#ffc107',
  '--warning-bg': '#fff3cd',
  '--color-warning-light': '#fff3cd',
  '--warning-border': '#ffeaa7',
  '--warning-text': '#856404',
  '--color-warning-dark': '#856404',
  
  // Error Colors
  '--error-color': '#e53e3e',
  '--color-error': '#e53e3e',
  '--error-bg': '#f8d7da',
  '--color-error-light': '#f8d7da',
  '--error-bg-light': '#fff5f5',
  '--error-text': '#721c24',
  '--color-error-dark': '#721c24',
  
  // Text Colors
  '--text-color': '#333',
  '--color-text-primary': '#333',
  '--color-text': '#333',
  '--text-muted': '#666',
  '--color-text-secondary': '#666',
  '--text-light': '#444',
  '--color-text-muted': '#999',
  
  // Border Colors
  '--border-color': '#e9ecef',
  '--color-border': '#e9ecef',
  '--border-light': '#ddd',
  '--border-lighter': '#eee',
  '--border-gray': '#e1e5e9',
  '--color-border-light': '#f1f3f4',
  
  // Background Colors
  '--background-light': '#f8f9fa',
  '--color-background-light': '#f8f9fa',
  '--background-white': '#fff',
  '--color-white': '#fff',
  '--color-bg': '#fff',
  '--background-gray': '#f8f8f8',
  '--background-color': '#f7f7f7',
  '--color-background-hover': '#f5f5f5',
  '--color-surface-alt': '#f8f9fa',
  
  // Badge Colors
  '--color-badge-bg': '#f8f9fa',
  '--color-badge-text': '#333',
  
  // Special Colors
  '--holiday-bg': '#fff0e6',
  '--holiday-color': '#dd6b20',
  '--weekend-bg': '#fff5f5',
  
  // On Primary (for text on primary background)
  '--color-on-primary': '#fff',
  
  // Shadow Colors
  '--shadow': 'rgba(0, 0, 0, 0.1)',
  '--shadow-light': 'rgba(0, 0, 0, 0.05)',
  '--shadow-medium': 'rgba(0, 0, 0, 0.15)',
  '--shadow-dark': 'rgba(0, 0, 0, 0.2)',
  
  // Overlay Colors
  '--white-overlay': 'rgba(255, 255, 255, 0.2)',
  '--white-overlay-light': 'rgba(255, 255, 255, 0.1)',
  '--color-white-overlay': 'rgba(255, 255, 255, 0.9)',
  '--color-white-overlay2': 'rgba(255, 255, 255, 0.2)',
  
  // Button States
  '--button-disabled-bg': 'rgb(245, 245, 245)',
  '--button-disabled-color': '#000',

  // pdf states
'--pdf-footer-line-bg': '#FFD700',

'--pdf-footer-title-color': '#FF6B81',

'--pdf-footer-sub-color': '#8D6E63',

'--pdf-footer-line-diff-bg': '#F5E8C8',

'--pdf-footer-title-diff-color': '#E65C70',

'--pdf-footer-sub-diffcolor': '#7A6658',

'--pdf-footer-line-second-bg': 'rgba(255, 255, 255, 0.80)',

'--pdf-title-text-color': '#8b4513',
'--pdf-title-text-color2': '#2d1b69'
};
// theme-check-enable

/**
 * 解析 CSS 变量为实际颜色值
 * @param cssValue CSS 值，可能包含 var() 函数
 * @returns 解析后的颜色值
 */
export function resolveCSSVariable(cssValue: string): string {
  if (!cssValue || typeof cssValue !== 'string') {
    return cssValue;
  }
  
  // 匹配 var(--variable-name, fallback) 格式
  const varMatch = cssValue.match(/var\(\s*(--[^,)]+)(?:\s*,\s*([^)]+))?\s*\)/);
  
  if (varMatch) {
    const [, variableName, fallback] = varMatch;
    const resolvedColor = COLOR_TOKENS[variableName];
    
    if (resolvedColor) {
      return resolvedColor;
    }
    
    // 如果有 fallback，递归解析 fallback
    if (fallback) {
      return resolveCSSVariable(fallback.trim());
    }
    
    // 如果没有找到对应的颜色，尝试从 DOM 中获取计算样式
    try {
      const computedStyle = getComputedStyle(document.documentElement);
      const computedValue = computedStyle.getPropertyValue(variableName);
      if (computedValue) {
        return computedValue.trim();
      }
    } catch (error) {
      console.warn(`无法解析 CSS 变量: ${variableName}`, error);
    }
    
    // 最后的 fallback，返回一个默认颜色
    return getDefaultColor(variableName);
  }
  
  return cssValue;
}

/**
 * 获取默认颜色（当无法解析 CSS 变量时使用）
 */
// theme-check-disable - 默认颜色fallback函数需要硬编码颜色值
function getDefaultColor(variableName: string): string {
  if (variableName.includes('primary')) return '#c41e3a';
  if (variableName.includes('success')) return '#28a745';
  if (variableName.includes('warning')) return '#ffc107';
  if (variableName.includes('error')) return '#e53e3e';
  if (variableName.includes('text')) return '#333';
  if (variableName.includes('border')) return '#e9ecef';
  if (variableName.includes('bg') || variableName.includes('background')) return '#fff';
  return '#333'; // 默认文本颜色
}
// theme-check-enable

/**
 * 解析颜色对象，将所有 CSS 变量替换为实际颜色值
 * @param colors 包含 CSS 变量的颜色对象
 * @returns 解析后的颜色对象
 */
export function resolveColorObject<T extends Record<string, string>>(colors: T): T {
  const resolved = {} as T;
  
  for (const [key, value] of Object.entries(colors)) {
    resolved[key as keyof T] = resolveCSSVariable(value) as T[keyof T];
  }
  
  return resolved;
}

/**
 * 检测当前主题模式
 * @returns 是否为深色模式
 */
export function isDarkMode(): boolean {
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return document.documentElement.classList.contains('dark') || prefersDark;
}

/**
 * 获取适配主题的颜色集合（用于 PDF 导出）
 * @returns 解析后的颜色对象
 */
export function getResolvedColors() {
  const colors = {
    bg: 'var(--color-bg)',
    text: 'var(--color-text)',
    subtle: 'var(--color-text-secondary)',
    card: 'var(--color-bg)',
    border: 'var(--color-border)',
    primary: 'var(--color-primary)',
    badgeBg: 'var(--color-badge-bg, var(--color-bg))',
    badgeText: 'var(--color-badge-text, var(--color-text))',
    rowAlt: 'var(--color-surface-alt, var(--color-bg))',
    accent: 'var(--color-primary)',
    onPrimary: 'var(--color-on-primary, #fff)',
    pdfFooterLineBg: 'var(--pdf-footer-line-bg)',
    pdfFooterTitleColor: 'var(--pdf-footer-title-color)',
    pdfFooterSubColor: 'var(--pdf-footer-sub-color)',
    pdfFooterLineDiffBg: 'var(--pdf-footer-line-diff-bg)',
    pdfFooterTitleDiffColor: 'var(--pdf-footer-title-diff-color)',
    pdfFooterSubDiffColor: 'var(--pdf-footer-sub-diff-color)',
    pdfFooterLineSecondBg: 'var(--pdf-footer-line-second-bg)',
    pdfTitleTextColor: 'var(--pdf-title-text-color)',
    pdfTitleTextColor2: 'var(--pdf-title-text-color2)',
  };
  
  return resolveColorObject(colors);
}
